import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import {
  Network,
  Upload,
  Download,
  Link2,
  Shield,
  Database,
  GitBranch,
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight,
  Info,
  ExternalLink,
  Layers,
  Sparkles,
  Building2,
  Zap,
  Share2,
} from 'lucide-react';

// Initialize mermaid with theme
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#0078D4',
    primaryTextColor: '#1F2937',
    primaryBorderColor: '#005A9E',
    lineColor: '#6B7280',
    secondaryColor: '#50E6FF',
    tertiaryColor: '#FFB900',
    background: '#FFFFFF',
    mainBkg: '#F3F4F6',
    nodeBorder: '#0078D4',
    clusterBkg: '#F0F9FF',
    titleColor: '#1F2937',
  },
  flowchart: {
    curve: 'basis',
    padding: 20,
  },
  sequence: {
    actorMargin: 50,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
    mirrorActors: false,
  },
});

interface Diagram {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
  mermaid: string;
}

const diagrams: Diagram[] = [
  {
    id: 'architecture',
    title: 'High-Level Architecture',
    icon: Network,
    description: 'Azure-native architecture with AI services for intelligent evidence management',
    mermaid: `flowchart TB
    subgraph Internet["Internet"]
        Users["👥 All Users"]
    end
    
    subgraph M365["Microsoft 365"]
        EntraID["🔐 Entra ID\\n(Identity Provider)"]
    end
    
    subgraph Azure["Azure Cloud"]
        subgraph Frontend["Frontend"]
            SWA["🌐 Static Web App\\n(React SPA)"]
        end
        
        subgraph Compute["Compute"]
            Functions["⚡ Azure Functions\\n(API + Blob Triggers)"]
        end
        
        subgraph Storage["Storage"]
            EvidenceStorage["📦 Evidence Blob\\n(WORM Immutable)"]
            FuncStorage["📁 Functions Storage"]
        end
        
        subgraph Data["Data"]
            SQL["🗄️ Azure SQL\\n(Metadata + Custody)"]
            AISearch["🔍 AI Search\\n(Vector + Full-text)"]
        end
        
        subgraph AI[" AI Services"]
            CU["🧠 Content Understanding\\n(Doc/Image/Video/Audio)"]
            OpenAI["💬 Azure OpenAI\\n(GPT-4o)"]
        end
        
        subgraph Ops["Operations"]
            LogAnalytics["📊 Log Analytics"]
            SignalR["📡 SignalR\\n(Real-time)"]
            KeyVault["🔑 Key Vault"]
        end
    end
    
    Users --> SWA
    SWA <--> EntraID
    SWA --> Functions
    SWA <--> SignalR
    
    Functions --> EvidenceStorage
    Functions --> SQL
    Functions --> AISearch
    Functions --> CU
    Functions --> OpenAI
    Functions --> KeyVault
    Functions --> LogAnalytics
    
    EvidenceStorage -.->|Blob Trigger| Functions
    CU --> AISearch`,
  },
  {
    id: 'upload',
    title: 'Evidence Upload Flow',
    icon: Upload,
    description: 'Complete sequence for evidence upload with SHA-256 hashing and chain of custody',
    mermaid: `sequenceDiagram
    participant U as 👤 User
    participant App as 📱 Web App
    participant Func as ⚡ Azure Function
    participant Blob as 📦 Blob Storage
    participant SQL as 🗄️ Azure SQL
    participant Log as 📊 Log Analytics

    rect rgb(230, 245, 255)
        Note over U,Func: Authentication
        U->>App: Navigate to upload
        App->>Func: POST /v1/evidence/upload
        Func->>Func: Validate token & role
    end

    rect rgb(255, 245, 230)
        Note over Func,Blob: Hash & Store
        Func->>Func: 🔐 Calculate SHA-256
        Func->>Blob: Upload to container
        Blob-->>Func: Version ID + ETag
    end

    rect rgb(255, 230, 245)
        Note over Func,SQL: Record Metadata
        Func->>SQL: INSERT EvidenceMetadata
        Func->>SQL: INSERT CustodyChainEvent
    end

    rect rgb(245, 245, 245)
        Note over Func,U: Audit & Response
        Func->>Log: Log upload event
        Func-->>App: 201 Created
        App-->>U: ✅ Upload confirmed
    end`,
  },
  {
    id: 'download',
    title: 'Evidence Download Flow',
    icon: Download,
    description: 'Download sequence with hash verification to detect tampering',
    mermaid: `sequenceDiagram
    participant U as 👤 User
    participant App as 📱 Web App
    participant Func as ⚡ Azure Function
    participant SQL as 🗄️ Azure SQL
    participant Blob as 📦 Blob Storage
    participant Log as 📊 Log Analytics

    rect rgb(230, 245, 255)
        Note over U,SQL: Authorization Check
        U->>App: Request download
        App->>Func: GET /evidence/{id}/download
        Func->>SQL: Check user case access
    end

    alt User has access
        rect rgb(255, 245, 230)
            Note over Func,Blob: Retrieve & Verify
            Func->>SQL: GET stored SHA-256 hash
            Func->>Blob: Download file
            Blob-->>Func: File stream
            Func->>Func: 🔐 Calculate SHA-256
        end

        alt Hash mismatch
            rect rgb(255, 200, 200)
                Note over Func,Log: TAMPERING DETECTED
                Func->>Log: 🚨 CRITICAL Alert
                Func->>SQL: Log TamperDetected event
                Func-->>App: 500 Integrity Failed
            end
        else Hash valid
            rect rgb(230, 255, 230)
                Note over Func,U: Successful Download
                Func->>SQL: Log Download event
                Func->>Log: Audit download
                Func-->>App: 200 OK + File
                App-->>U: ✅ Evidence delivered
            end
        end
    else No access
        Func->>Log: 🚨 Unauthorized attempt
        Func-->>App: 403 Forbidden
    end`,
  },
  {
    id: 'custody',
    title: 'Chain of Custody Lifecycle',
    icon: Link2,
    description: 'Evidence state transitions and custody event types',
    mermaid: `stateDiagram-v2
    [*] --> Uploaded: Evidence submitted
    
    Uploaded --> Active: Case assigned
    Uploaded --> Pending: Awaiting review
    
    Pending --> Active: Approved
    Pending --> Rejected: Invalid submission
    
    Active --> Accessed: Downloaded/Viewed
    Accessed --> Active: Access complete
    
    Active --> Shared: Shared with party
    Shared --> Active: Share recorded
    
    Active --> LegalHold: Court order received
    LegalHold --> Active: Hold released
    
    Active --> Redacted: Redaction applied
    Redacted --> Active: Redaction complete
    
    Active --> Released: Case closed
    Released --> [*]: Retention expired
    
    Rejected --> [*]: Removed`,
  },
  {
    id: 'ai-pipeline',
    title: 'AI Evidence Pipeline',
    icon: Sparkles,
    description: 'Content Understanding → AI Search → Agent Aegis intelligence flow',
    mermaid: `flowchart TB
    subgraph Upload["📤 Evidence Upload"]
        Blob["Blob Storage\\n(WORM)"]
    end
    
    Blob -->|Blob Trigger| Trigger["⚡ Azure Function"]
    
    subgraph CU["🧠 CONTENT UNDERSTANDING"]
        CUDoc["📄 Documents\\nLayout, tables, entities, signatures"]
        CUImg["🖼️ Images\\nObjects, OCR, scene description"]
        CUVid["🎬 Video\\nTranscript, faces, objects, plates"]
        CUAud["🎤 Audio\\nTranscription, speaker diarization"]
    end
    
    Trigger --> CU
    
    subgraph Index["📊 INDEXED DATA"]
        SQL["🗄️ SQL DB\\nMetadata + Summary"]
        Search["🔍 AI Search\\nVector + Full-text"]
        Extracted["📦 Blob\\nExtracted JSON"]
    end
    
    CU --> SQL
    CU --> Search
    CU --> Extracted
    
    subgraph Aegis["🛡️ AGENT AEGIS (GPT-4o)"]
        NLQ["Natural language Q&A"]
        Cross["Cross-evidence correlation"]
        Vision["Real-time vision queries"]
        Reports["Report generation"]
    end
    
    Search --> Aegis
    SQL --> Aegis
    
    style CU fill:#E8F4FD,stroke:#0078D4
    style Aegis fill:#FFF4E5,stroke:#FF8C00
    style Index fill:#E8F8E8,stroke:#107C10`,
  },
  {
    id: 'auth',
    title: 'Authentication Flows',
    icon: Shield,
    description: 'Entra ID OAuth 2.0 and demo PIN authentication',
    mermaid: `sequenceDiagram
    participant U as 👤 User
    participant App as 🌐 Application
    participant Entra as 🔐 Entra ID
    participant API as ⚡ Azure Functions

    U->>App: 1. Access protected resource
    App->>Entra: 2. Redirect to /authorize
    
    rect rgb(255, 245, 230)
        Note over U,Entra: User Authentication
        Entra->>U: 3. Login page
        U->>Entra: 4. Credentials + MFA
        Entra->>Entra: 5. Validate credentials
    end
    
    Entra->>App: 6. Authorization code
    App->>Entra: 7. POST /token
    Entra-->>App: 8. Access + ID tokens
    
    rect rgb(230, 255, 230)
        Note over App,API: API Authorization
        App->>API: 9. GET /v1/evidence
        API->>API: 10. Validate token
        API->>API: 11. Check role claim
        API-->>App: 12. Protected resource
    end`,
  },
  {
    id: 'security',
    title: 'Security Zones',
    icon: Shield,
    description: 'Trust boundaries and data flow security layers',
    mermaid: `flowchart TB
    subgraph Internet["🌐 UNTRUSTED - Internet"]
        Users["👥 All Users"]
    end
    
    subgraph AuthBoundary["🔐 AUTHENTICATION BOUNDARY"]
        EntraID["Entra ID\\n(Organizational)"]
        B2C["Entra ID B2C\\n(Public)"]
    end
    
    subgraph AppBoundary["📱 APPLICATION BOUNDARY"]
        SWA["Static Web App"]
        Functions["Azure Functions\\n• Token validation\\n• RBAC enforcement\\n• Input sanitization"]
    end
    
    subgraph DataBoundary["🗄️ DATA BOUNDARY"]
        Blob["Blob Storage\\n🔒 WORM\\n🚫 No public access"]
        SQL["Azure SQL\\n🔐 Entra ID auth\\n🔒 TDE encryption"]
        KV["Key Vault\\n🔐 Managed Identity"]
    end
    
    subgraph AuditBoundary["📊 AUDIT BOUNDARY"]
        LogAnalytics["Log Analytics\\n365-day retention"]
        AppInsights["App Insights"]
    end
    
    Users --> AuthBoundary
    AuthBoundary --> AppBoundary
    AppBoundary --> DataBoundary
    AppBoundary --> AuditBoundary
    DataBoundary --> AuditBoundary`,
  },
  {
    id: 'database',
    title: 'Database Schema',
    icon: Database,
    description: 'Entity relationship diagram for evidence metadata and custody events',
    mermaid: `erDiagram
    EvidenceMetadata ||--o{ CustodyChainEvents : "has"
    EvidenceMetadata {
        uuid id PK
        string blobUrl
        string sha256Hash
        string originalFileName
        bigint fileSizeBytes
        string mimeType
        string evidenceType
        string uploadedById
        string uploadedByRole
        datetime uploadedAt
        string caseId
        string description
        string sourcePortal
        boolean isUnderLegalHold
        string legalHoldOrderNumber
    }
    
    CustodyChainEvents {
        uuid id PK
        uuid evidenceId FK
        string eventType
        string actorId
        string actorRole
        datetime timestamp
        string ipAddress
        string userAgent
        string details
    }
    
    DemoUsers {
        uuid id PK
        string pin UK
        string name
        string role
        string email
        string department
    }`,
  },
  {
    id: 'pipeline',
    title: 'Deployment Pipeline',
    icon: GitBranch,
    description: 'CI/CD flow for infrastructure and application deployment',
    mermaid: `flowchart LR
    subgraph Dev["👨‍💻 Development"]
        Code["Code Changes"]
        PR["Pull Request"]
    end
    
    subgraph CI["🔄 CI Pipeline"]
        Build["npm run build"]
        Test["npm test"]
        Lint["ESLint + Prettier"]
    end
    
    subgraph Infra["🏗️ Infrastructure"]
        Bicep["Bicep Validate"]
        WhatIf["What-If"]
    end
    
    subgraph CD["🚀 CD Pipeline"]
        DeployInfra["Deploy Bicep"]
        DeployFunc["Deploy Functions"]
        DeploySWA["Deploy SWA"]
    end
    
    subgraph Envs["🌍 Environments"]
        DevEnv["Dev"]
        Staging["Staging"]
        Prod["Production"]
    end
    
    Code --> PR
    PR --> Build
    Build --> Test
    Test --> Lint
    Lint --> Bicep
    Bicep --> WhatIf
    
    WhatIf --> DeployInfra
    DeployInfra --> DeployFunc
    DeployFunc --> DeploySWA
    
    DeploySWA --> DevEnv
    DevEnv -->|Promote| Staging
    Staging -->|Approve| Prod`,
  },
  {
    id: 'caf-alignment',
    title: 'CAF Platform Alignment',
    icon: Building2,
    description: 'DEMS mapped to Microsoft Cloud Adoption Framework unified data platform (aka.ms/cafdata)',
    mermaid: `flowchart TB
    subgraph GOV["🔐 GOVERNANCE & SECURITY — Entire Organization"]
        direction LR
        Entra["Microsoft Entra\\n━━━━━━━━━━━\\nDEMS: Entra ID Auth\\nRBAC • B2C Public Portal\\n✅ BUILT"]
        Agent365["Microsoft Agent 365\\n━━━━━━━━━━━\\nDEMS: Agent Aegis\\nGPT-4o Evidence AI\\n✅ BUILT"]
        Purview["Microsoft Purview\\n━━━━━━━━━━━\\nDEMS: Chain of Custody\\nWORM • Audit Logs\\n✅ BUILT"]
        Defender["Microsoft Defender\\n━━━━━━━━━━━\\nDEMS: WAF • DDoS\\nThreat Protection\\n🔜 READY"]
    end

    subgraph AZURE["☁️ AZURE"]
        subgraph PLZ["Platform Landing Zone — Governance & Centralized Resources"]
            Bicep["Bicep IaC\\n20+ modules"]
            RBAC2["Custom RBAC\\n5 roles"]
            Monitor["Azure Monitor\\nLog Analytics"]
        end

        subgraph ALZ["Application Landing Zone — DEMS Workloads"]
            subgraph Core["Core Application ✅"]
                SWA["Static Web App\\nReact SPA"]
                Func["Azure Functions\\nTypeScript API"]
                SQL["Azure SQL\\nMetadata + Custody"]
                Blob["Blob Storage\\nWORM Immutable"]
            end
            subgraph AI["AI Services ✅"]
                CU["Content Understanding\\nDoc • Image • Video"]
                Search["AI Search\\nVector + Full-text"]
                OpenAI["Azure OpenAI\\nGPT-4o • Agent Aegis"]
            end
            subgraph Foundry["Microsoft Foundry 🔜"]
                FoundryIQ["Foundry IQ\\nAgent grounded on\\nevidence data products"]
            end
        end
    end

    subgraph FABRIC["📊 MICROSOFT FABRIC — Data Products & Analytics"]
        direction LR
        CopilotStudio["Copilot Studio\\n━━━━━━━━━━━\\nEvidence Intake Bot\\nVictim Notification\\n🔜 READY"]
        FabricIQ["Fabric IQ\\n━━━━━━━━━━━\\nCross-Case Analytics\\nDisposition Reporting\\n🔜 READY"]
        M365Copilot["M365 Copilot\\n━━━━━━━━━━━\\nProsecutor Briefings\\nCase Summarization\\n🔜 READY"]
    end

    subgraph PURVIEW_SPAN["🛡️ MICROSOFT PURVIEW — Compliance Across All Data"]
        PurviewGov["Evidence Chain of Custody • WORM Immutability • Legal Hold • Retention Policies"]
    end

    subgraph SOURCES["📂 ALL DATA SOURCES"]
        direction LR
        OnPrem["🏢 On-Premises\\n━━━━━━━━━━━\\nBody Cameras\\nLegacy RMS\\nCourt Recording"]
        Dataverse["📋 Dataverse\\n━━━━━━━━━━━\\nPower Automate\\nIntake Workflows\\nVictim Portal"]
        AzureDS["☁️ Azure\\n━━━━━━━━━━━\\nBlob Storage\\nAzure SQL\\nAI Search Index"]
        ExtCloud["🌐 External\\n━━━━━━━━━━━\\nCMS (Tyler)\\nLaw Enforcement\\nDA Office"]
    end

    GOV ~~~ AZURE
    AZURE ~~~ FABRIC
    FABRIC ~~~ PURVIEW_SPAN
    PURVIEW_SPAN ~~~ SOURCES

    style GOV fill:#E8F0FE,stroke:#1A73E8,stroke-width:2px
    style AZURE fill:#F0F9FF,stroke:#0078D4,stroke-width:2px
    style PLZ fill:#E8F4FD,stroke:#005A9E
    style ALZ fill:#F0FFF0,stroke:#107C10
    style Core fill:#E8FFE8,stroke:#107C10
    style AI fill:#FFF4E5,stroke:#FF8C00
    style Foundry fill:#F3E8FF,stroke:#7B2FF7
    style FABRIC fill:#F5F0FF,stroke:#7B2FF7,stroke-width:2px
    style PURVIEW_SPAN fill:#FFF8E1,stroke:#F57C00,stroke-width:2px
    style SOURCES fill:#FAFAFA,stroke:#9E9E9E,stroke-width:2px`,
  },
  {
    id: 'event-grid',
    title: 'Event Grid Backbone',
    icon: Zap,
    description: 'Current blob triggers vs. target Event Grid architecture for event-driven evidence processing',
    mermaid: `flowchart TB
    subgraph Current["📌 CURRENT — Blob Triggers (Built)"]
        direction LR
        Upload1["📤 Upload API"] --> Blob1["📦 Blob Storage"]
        Blob1 -->|"Blob Trigger\\n(polling)"| Func1["⚡ Hash Function"]
        Func1 --> SQL1["🗄️ SQL Update"]
        Func1 --> CU1["🧠 Content Understanding"]
    end

    subgraph Target["🎯 TARGET — Event Grid (Platform-Ready)"]
        Upload2["📤 Upload API"] --> Blob2["📦 Blob Storage"]
        Blob2 -->|"BlobCreated\\nevent"| EG["⚡ Event Grid\\nTopic"]
        
        EG -->|"Subscribe"| Hash["🔐 Hash & Verify\\nFunction"]
        EG -->|"Subscribe"| AIProc["🧠 AI Processing\\nFunction"]
        EG -->|"Subscribe"| Notify["📡 SignalR\\nNotification"]
        EG -->|"Subscribe"| Audit["📊 Audit Log\\nFunction"]
        EG -->|"Subscribe"| CMS["🏛️ CMS Sync\\nFunction"]
        EG -->|"Subscribe"| LENotify["👮 Law Enforcement\\nNotification"]
        
        Hash --> SQL2["🗄️ SQL"]
        AIProc --> Search2["🔍 AI Search"]
        Notify --> Users2["👥 Users"]
        CMS --> Tyler["Tyler Odyssey"]
    end

    Current ~~~|"Migration Path"| Target

    style Current fill:#FFF3E0,stroke:#E65100,stroke-width:2px
    style Target fill:#E8F5E9,stroke:#1B5E20,stroke-width:2px
    style EG fill:#E3F2FD,stroke:#0D47A1,stroke-width:3px`,
  },
  {
    id: 'integration-patterns',
    title: 'Integration Patterns',
    icon: Share2,
    description: 'Three patterns for connecting DEMS to CMS, law enforcement, DA offices, and court systems',
    mermaid: `flowchart TB
    subgraph P1["⚡ Pattern 1: Event-Driven (Real-time)"]
        direction LR
        EG1["Event Grid"] -->|"EvidenceFiled"| CMS1["CMS System"]
        EG1 -->|"LegalHoldApplied"| DA1["DA Office"]
        EG1 -->|"NewEvidence"| LE1["Law Enforcement"]
        CMS1 -->|"CaseUpdated"| EG1
    end

    subgraph P2["🔌 Pattern 2: API (Request/Response)"]
        direction LR
        DEMS2["DEMS API\\n/v1/evidence\\n/v1/cases\\n/v1/custody"] <-->|"REST + OAuth 2.0"| Consumers2["CMS • DA Portal\\nDefense Portal\\nCourt Clerk"]
        APIM["API Management\\nRate Limiting\\nAPI Keys"] --- DEMS2
    end

    subgraph P3["📦 Pattern 3: Batch (Scheduled)"]
        direction LR
        Schedule["⏰ Scheduled\\nFunction"] --> Export["📤 Export\\nEvidence Package"]
        Export --> SFTP["SFTP / Blob"]
        SFTP --> Legacy["Legacy Systems\\nOn-Prem RMS\\nState Repository"]
    end

    subgraph Mapping["🗺️ System Integration Map"]
        direction LR
        Tyler["🏛️ Tyler Odyssey\\nAPI + Events"] ---|"Cases, Filings"| DEMS_Core["DEMS"]
        LEAgency["👮 Law Enforcement\\nAPI + SFTP"] ---|"Evidence, Reports"| DEMS_Core
        DAOffice["⚖️ DA Office\\nAPI + Events"] ---|"Discovery, Holds"| DEMS_Core
        CourtRec["🎥 Court Recording\\nEvents + Batch"] ---|"A/V, Transcripts"| DEMS_Core
        PubDef["🛡️ Public Defender\\nAPI read-only"] ---|"Discovery Access"| DEMS_Core
    end

    style P1 fill:#E3F2FD,stroke:#0D47A1,stroke-width:2px
    style P2 fill:#E8F5E9,stroke:#1B5E20,stroke-width:2px
    style P3 fill:#FFF3E0,stroke:#E65100,stroke-width:2px
    style Mapping fill:#F3E5F5,stroke:#7B1FA2,stroke-width:2px`,
  },
];

function MermaidDiagram({ chart, id }: { chart: string; id: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current) return;
      
      try {
        // Clear previous content
        containerRef.current.innerHTML = '';
        
        // Generate unique ID for this render
        const uniqueId = `mermaid-${id}-${Date.now()}`;
        
        // Render the diagram
        const { svg } = await mermaid.render(uniqueId, chart);
        containerRef.current.innerHTML = svg;
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError('Failed to render diagram');
      }
    };

    renderDiagram();
  }, [chart, id]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg">
        <div className="text-red-600 text-center">
          <Info className="w-8 h-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="mermaid-container flex items-center justify-center min-h-[400px] overflow-auto"
    />
  );
}

export default function Diagrams() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentDiagram = diagrams[currentIndex];
  const Icon = currentDiagram.icon;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? diagrams.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === diagrams.length - 1 ? 0 : prev + 1));
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      try {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Fullscreen error:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (err) {
        console.error('Exit fullscreen error:', err);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape' && isFullscreen) {
        document.exitFullscreen();
      }
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600 rounded-lg">
              <Layers className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Architecture Diagrams</h1>
              <p className="text-slate-300">Interactive Mermaid diagrams for DEMS system architecture</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://github.com/Millar-Time/Justice-DEMS/blob/main/docs/diagrams.md"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              View Source
            </a>
          </div>
        </div>
      </div>

      {/* Diagram Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border mb-6 overflow-x-auto">
        <div className="flex p-2 gap-1 min-w-max">
          {diagrams.map((diagram, index) => {
            const DiagramIcon = diagram.icon;
            const isActive = index === currentIndex;
            return (
              <button
                key={diagram.id}
                onClick={() => setCurrentIndex(index)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <DiagramIcon className="w-4 h-4" />
                <span className="text-sm font-medium">{diagram.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Diagram Display */}
      <div
        ref={containerRef}
        className={`bg-white rounded-xl shadow-lg border transition-all ${
          isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''
        }`}
      >
        {/* Diagram Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isFullscreen ? 'bg-white' : ''}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">{currentDiagram.title}</h2>
              <p className="text-sm text-slate-500">{currentDiagram.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400 mr-4">
              {currentIndex + 1} / {diagrams.length}
            </span>
            <button
              onClick={handlePrevious}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Previous (←)"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <button
              onClick={handleNext}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Next (→)"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors ml-2"
              title="Fullscreen (F)"
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5 text-slate-600" />
              ) : (
                <Maximize2 className="w-5 h-5 text-slate-600" />
              )}
            </button>
          </div>
        </div>

        {/* Diagram Content */}
        <div className={`p-6 ${isFullscreen ? 'h-[calc(100vh-80px)] overflow-auto' : ''}`}>
          <MermaidDiagram chart={currentDiagram.mermaid} id={currentDiagram.id} />
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="mt-6 bg-slate-50 rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="text-sm text-slate-600">
            <span className="font-medium">Keyboard shortcuts:</span>{' '}
            <span className="px-2 py-0.5 bg-white rounded border text-xs">←</span>{' '}
            <span className="px-2 py-0.5 bg-white rounded border text-xs">→</span> Navigate{' '}
            <span className="px-2 py-0.5 bg-white rounded border text-xs">F</span> Fullscreen{' '}
            <span className="px-2 py-0.5 bg-white rounded border text-xs">Esc</span> Exit fullscreen
          </div>
          <div className="flex items-center gap-2">
            {diagrams.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex ? 'bg-blue-600 scale-125' : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Deployment Info */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="text-sm text-slate-500">Region</div>
          <div className="font-semibold text-slate-800">West US 3</div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="text-sm text-slate-500">Resource Group</div>
          <div className="font-semibold text-slate-800">rg-Justice-DEMS-v2</div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="text-sm text-slate-500">Function App</div>
          <div className="font-semibold text-slate-800">func-dems-api-dev</div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="text-sm text-slate-500">Static Web App</div>
          <div className="font-semibold text-slate-800">swa-dems-dev</div>
        </div>
      </div>
    </div>
  );
}
